import JSONRPC from './JSONRPC';
import type { Refl_Type } from 'innovatint_base/common/color_calc/color_conv';
import type {
  CalibrationStep,
  JsonRpcTransport,
  SpectroConfig,
  SpectroDownloadedMeasurement,
  SpectroInstallPrompt,
  SpectroMeasureResult,
  SpectroModelInfo,
  SpectroQtObject,
  SpectroResultCode,
  SpectroStatus,
} from './types/Spectro';

export type {
  CalibrationStep,
  InstallStep,
  JsonRpcTransport,
  SpectroConfig,
  SpectroDownloadedMeasurement,
  SpectroDriverInfo,
  SpectroInstallPrompt,
  SpectroMeasureResult,
  SpectroMeasurementData,
  SpectroModelInfo,
  SpectroQtObject,
  SpectroResultCode,
  SpectroStatus,
  SpectroTriplet,
  TriggerMode,
} from './types/Spectro';

export const consts = {
  CALIB_GUI: 0, //: separate GUI will appear to control calibration
  CALIB_BLACK: 1, //: black calibration using black trap
  CALIB_WHITE: 2, //: white calibration using white tile
  CALIB_GREY: 3, //: grey calibration
  CALIB_CHECK_GREEN: 4, //: calibration check using green tile
  CALIB_CLOSE_COVER: 5, //: calibration, user must close
  CALIB_UV1: 6, //: ultraviolet calibration
  CALIB_UV2: 7,
  CALIB_BLACK_OPEN: 8, //Instructs user to use black trap or open port
  CALIB_CHECK_BLUE: 9, //: calibration check using blue tile
  CALIB_AUTO: 10, //instrument can calibrate itself without help from user
  CALIB_DEVICE_UI: 11, //user must follow directions shown on device
  CALIB_SIDESENSOR: 12, // Move slider to normal measurement position

  TRIGGER_CALIB_SW: 0, //calibration measurement can be initiated from software
  TRIGGER_CALIB_DEVICE: 1, //calibration measurement can be initiated on device
  TRIGGER_MEAS_SW: 2, //measurement can be initiated from software
  TRIGGER_MEAS_DEVICE: 3, //measurement can be initiated on device

  INSTALL_UNIT_DRIVER_FILES: 0,
  INSTALL_WHITE_STANDARD_FILES: 1,
  INSTALL_LICENSE_FILE: 2,

  RESULT_OK: 0,
  RESULT_ERROR: 1,
  RESULT_TIMEOUT: 2,
  RESULT_UNKNOWN: 3, //calibration ended
} as const;

export class SpectroAPI {
  private jsonrpc: JsonRpcTransport;

  constructor() {
    // dummy dispatcher that resolves all calls to null
    this.jsonrpc = {
      request: <T>() => Promise.resolve(null as T),
      handleResponse: () => undefined,
    };
  }

  connect(qtobject: SpectroQtObject): void {
    try {
      this.jsonrpc = new JSONRPC((message) => {
        qtobject.dispatch(JSON.stringify(message));
      });
      qtobject.response.connect((str) =>
        this.jsonrpc.handleResponse(JSON.parse(str))
      );
    } catch (e) {
      console.log('Error in Spectro Qt connection');
      console.log(e);
    }
  }

  // all methods return promises
  getConfig(): Promise<SpectroConfig | null> {
    return this.jsonrpc.request<SpectroConfig | null>('getConfig');
  }

  getModelInfo(): Promise<SpectroModelInfo | null> {
    return this.jsonrpc.request<SpectroModelInfo | null>('getModelInfo');
  }

  setDriver(name: string): Promise<SpectroConfig> {
    return this.jsonrpc.request<SpectroConfig>('setDriver', [name]);
  }

  setPort(name: string, address?: string | null): Promise<SpectroConfig> {
    return this.jsonrpc.request<SpectroConfig>('setPort', [name, address]);
  }

  configure(): Promise<SpectroConfig> {
    return this.jsonrpc.request<SpectroConfig>('configure');
  }

  getStatus(): Promise<SpectroStatus> {
    return this.jsonrpc.request<SpectroStatus>('getStatus');
  }

  installPrompts(steps: SpectroInstallPrompt[]): Promise<null> {
    return this.jsonrpc.request<null>('installPrompts', [steps]);
  }

  calibrate(
    step: CalibrationStep,
    waitForTrigger: boolean
  ): Promise<SpectroResultCode> {
    const timeout = 30;
    return this.jsonrpc.request<SpectroResultCode>('calibrate', [
      step,
      waitForTrigger,
      timeout,
    ]);
  }

  measure(
    waitForTrigger: boolean,
    gloss: number | null = null
  ): Promise<SpectroMeasureResult> {
    const timeout = 30;
    const params =
      gloss == null
        ? [waitForTrigger, timeout] // this case is compatible with older pos app
        : [waitForTrigger, timeout, gloss];
    return this.jsonrpc.request<SpectroMeasureResult>('measure', params);
  }

  setMode(
    aperture: string,
    specularMode: string,
    uvMode: string
  ): Promise<SpectroResultCode> {
    return this.jsonrpc.request<SpectroResultCode>('set_mode', [
      aperture,
      specularMode,
      uvMode,
    ]);
  }

  download(): Promise<SpectroDownloadedMeasurement[]> {
    return this.jsonrpc.request<SpectroDownloadedMeasurement[]>('download');
  }

  clearStored(): Promise<null> {
    return this.jsonrpc.request<null>('clear_stored');
  }

  refl_045_to_sci(
    refl: Refl_Type,
    gloss: number
  ): Promise<SpectroMeasureResult> {
    return this.jsonrpc.request<SpectroMeasureResult>('refl_045_to_sci', [
      refl,
      gloss,
    ]);
  }
}

const api = new SpectroAPI();

export { api as default };
